import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../api";
import { MEASUREMENT_FIELDS, type EmergencyBody, type Measurement } from "../../types";
import { tg } from "../../telegram";
import { useT } from "../../i18n";
import MeasurementFieldRow, { NumberStepper } from "../../components/MeasurementFieldRow";

const REASON_MAX = 500;
// KW is the only always-shown core field now (KV was dropped from the SHPP form).
const CORE_KEYS = new Set(["kw"]);

export default function EmergencyForm() {
  const { t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const turbineId = parseInt(params.get("turbine_id") || "", 10);

  const [kw, setKw] = useState("");
  const [reason, setReason] = useState("");
  // Extra (non-core) field values, keyed by MEASUREMENT_FIELDS[].key.
  const [extras, setExtras] = useState<Record<string, string>>({});
  const [showExtras, setShowExtras] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const extraFields = useMemo(
    () => MEASUREMENT_FIELDS.filter(f => !CORE_KEYS.has(f.key)),
    [],
  );
  const extrasFilled = Object.values(extras).filter(v => v.trim() !== "").length;

  const reasonTrim = reason.trim();
  const reasonOk = reasonTrim.length > 0 && reasonTrim.length <= REASON_MAX;
  const canSubmit = reasonOk && !submitting && !!turbineId;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    tg()?.HapticFeedback?.impactOccurred("medium");
    try {
      const extra_values: Record<string, string> = {};
      for (const f of extraFields) {
        const v = extras[f.key]?.trim();
        if (v) extra_values[f.key] = v;
      }
      const body: EmergencyBody = {
        turbine_id: turbineId,
        kw: kw.trim() || undefined,
        reason: reasonTrim,
        extra_values: Object.keys(extra_values).length ? extra_values : undefined,
      };
      await api.post<Measurement>("/api/me/emergency", body);
      tg()?.HapticFeedback?.notificationOccurred("success");
      navigate("/report", { replace: true });
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred("error");
      setError((e as ApiError).message);
      setSubmitting(false);
    }
  };

  const cancel = () => {
    tg()?.HapticFeedback?.impactOccurred("light");
    navigate("/report", { replace: true });
  };

  const setExtra = (key: string, raw: string) =>
    setExtras(prev => ({ ...prev, [key]: raw }));

  return (
    <div>
      <h1>{t("emergency.title")}</h1>
      <div className="report-status-hint" style={{ marginBottom: 16 }}>
        {t("emergency.intro")}
      </div>

      {error && <div className="alert error">{error}</div>}

      <label className="report-form-row" style={{ display: "block", padding: "12px" }}>
        <div className="report-form-label" style={{ marginBottom: 6 }}>
          {t("emergency.reason_label")} <span style={{ color: "var(--destructive)" }}>*</span>
        </div>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={t("emergency.reason_placeholder")}
          maxLength={REASON_MAX}
          rows={3}
          style={{ width: "100%", resize: "vertical", minHeight: 70 }}
          autoFocus
        />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--hint)", marginTop: 4 }}>
          <span>
            {reasonOk ? "✓" : (reasonTrim.length === 0 ? t("emergency.reason_required") : "")}
          </span>
          <span>{reason.length}/{REASON_MAX}</span>
        </div>
      </label>

      <div className="report-form" style={{ marginTop: 12 }}>
        <label className="report-form-row">
          <span className="report-form-label">KW</span>
          <NumberStepper value={kw} onChange={setKw} />
        </label>
      </div>

      {/* Collapsible: all the other indicators if the operator has time. */}
      <button
        type="button"
        className="btn secondary disclosure"
        onClick={() => setShowExtras(s => !s)}
        style={{ marginTop: 12 }}
      >
        <span>{showExtras ? "▴" : "▾"}</span>
        <span style={{ flex: 1, textAlign: "start", marginLeft: 8 }}>
          {showExtras ? t("emergency.hide_extras") : t("emergency.show_extras")}
        </span>
        {extrasFilled > 0 && (
          <span style={{ fontSize: 12, color: "var(--hint)" }}>
            {extrasFilled}/{extraFields.length}
          </span>
        )}
      </button>

      {showExtras && (
        <div className="report-form" style={{ marginTop: 10 }}>
          {extraFields.map(f => (
            <MeasurementFieldRow
              key={f.key}
              field={f}
              value={extras[f.key] ?? ""}
              onChange={v => setExtra(f.key, v)}
            />
          ))}
        </div>
      )}

      <div className="report-form-actions">
        <button
          className="big-btn primary"
          disabled={!canSubmit}
          onClick={submit}
        >
          <span className="big-btn-icon">📤</span>
          <span className="big-btn-text">
            <b>{submitting ? t("report_form.submitting") : t("report_form.submit")}</b>
            <small>{reasonOk ? t("emergency.submit_sub_ok") : t("emergency.submit_sub_disabled")}</small>
          </span>
        </button>
        <button className="big-btn danger-outline" onClick={cancel} disabled={submitting}>
          <span className="big-btn-icon">✖</span>
          <span className="big-btn-text">
            <b>{t("report_form.cancel")}</b>
            <small>{t("report_form.cancel_sub")}</small>
          </span>
        </button>
      </div>
    </div>
  );
}
